import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from './firebase';
import './LimitCheckModal.css';


const LimitCheckModal = ({ 
  isOpen, 
  onClose, 
  onProceed, 
  selectedTimeSlot, 
  selectedDate, 
  selectedLocation, 
  appointmentType,
  sessionId 
}) => {
  const [checking, setChecking] = useState(true);
  const [limitReached, setLimitReached] = useState(false);
  const [error, setError] = useState(null);

  // Perform limit check when modal opens
  useEffect(() => {
    if (isOpen && selectedTimeSlot) {
      performLimitCheck();
    }
  }, [isOpen, selectedTimeSlot]);

  const performLimitCheck = async () => {
    try {
      setChecking(true);
      setError(null);

      // Query orders for this date, location, appointment type, and time
      const ordersQuery = query(
        collection(db, 'orders'),
        where('appointmentDate', '==', selectedDate),
        where('location', '==', selectedLocation),
        where('appointmentType', '==', appointmentType),
        where('appointmentTime', '==', selectedTimeSlot),
        where('status', 'in', ['confirmed', 'trading'])
      );

      // Query active sessions for this date, location, appointment type, and time
      const now = new Date();
      const activeSessionsQuery = query(
        collection(db, 'activeBookings'),
        where('appointmentDate', '==', selectedDate),
        where('location', '==', selectedLocation),
        where('appointmentType', '==', appointmentType),
        where('appointmentTime', '==', selectedTimeSlot),
        where('expiresAt', '>', now)
      );

      const [ordersSnapshot, sessionsSnapshot] = await Promise.all([
        getDocs(ordersQuery),
        getDocs(activeSessionsQuery)
      ]);

      // Count orders and sessions (excluding current user's session)
      let orderCount = 0;
      let sessionCount = 0;

      ordersSnapshot.forEach(doc => {
        const order = doc.data();
        if (order.userId !== auth.currentUser?.uid) {
          orderCount++;
        }
      });

      sessionsSnapshot.forEach(doc => {
        const session = doc.data();
        if (session.userId !== auth.currentUser?.uid) {
          sessionCount++;
        }
      });

      const totalUsers = orderCount + sessionCount;
      const maxUsers = appointmentType === 'special' ? 3 : 1; // Special: 3 users, Normal/free: 1 user per slot

      console.log('🔍 Limit check results:', {
        selectedTimeSlot,
        orderCount,
        sessionCount,
        totalUsers,
        maxUsers,
        limitReached: totalUsers >= maxUsers
      });

      if (totalUsers >= maxUsers) {
        setLimitReached(true);
      } else {
        setLimitReached(false);
      }

    } catch (error) {
      console.error('Error performing limit check:', error);
      setError('Failed to check availability. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const handleProceed = () => {
    onProceed(selectedTimeSlot, sessionId);
  };

  const handleSelectNewTime = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="limit-check-modal-overlay">
      <div className="limit-check-modal">
        <div className="limit-check-modal-header">
          <h2>Checking Availability</h2>
        </div>
        
        <div className="limit-check-modal-content">
          {checking ? (
            <div className="checking-content">
              <div className="loading-spinner"></div>
              <p>Verifying timeslot availability...</p>
            </div>
          ) : error ? (
            <div className="error-content">
              <p className="error-message">{error}</p>
              <button onClick={handleSelectNewTime} className="retry-button">
                Try Again
              </button>
            </div>
          ) : limitReached ? (
            <div className="limit-reached-content">
              <div className="warning-icon">⚠️</div>
              <h3>Timeslot No Longer Available</h3>
              <p>This timeslot has been taken by another user while you were selecting it.</p>
              <p><strong>Please select a different time.</strong></p>
              <button onClick={handleSelectNewTime} className="select-new-time-button">
                Select New Time
              </button>
            </div>
          ) : (
            <div className="available-content">
              <div className="success-icon">✅</div>
              <h3>Timeslot Available!</h3>
              <p>Your selected time is still available.</p>
              <p><strong>Time:</strong> {selectedTimeSlot}</p>
              <button onClick={handleProceed} className="proceed-button">
                Continue with Booking
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LimitCheckModal;
